import { useState } from 'react'

/**
 * A team's badge, or its initials when there isn't one.
 *
 * Roughly a fifth of teams in OpenDota's directory have no logo, and the URLs that do exist point
 * at two different Valve CDNs — one of which serves PNGs as `application/octet-stream`. So this
 * never gates on content type, and it treats a missing or broken image as ordinary rather than as
 * an error: the fallback is drawn from the team name so every row keeps the same shape and nothing
 * jumps when an image 404s.
 *
 * `loading="lazy"` because a busy schedule page carries a few dozen of these, all from a third-party
 * host we do not control the latency of.
 */
export function TeamBadge({ name, logo, size = 24 }: {
  name: string | null
  logo: string | null | undefined
  /** Rendered height in CSS pixels. Width follows the logo's own shape. */
  size?: number
}) {
  const [broken, setBroken] = useState(false)

  if (!logo || broken) {
    // The fallback stays square, because two letters have no aspect ratio to respect.
    return (
      <span
        aria-hidden="true"
        style={{ width: size, height: size }}
        className="inline-flex shrink-0 items-center justify-center rounded bg-surface-raised text-xs font-semibold uppercase leading-none tracking-tight text-ink-faint"
      >
        {initials(name)}
      </span>
    )
  }

  return (
    <img
      src={logo}
      // Empty alt with the name already beside it: a screen reader announcing the team twice is
      // worse than not announcing the badge at all.
      alt=""
      aria-hidden="true"
      loading="lazy"
      // Height is fixed and width follows the image, rather than both being pinned to a square.
      //
      // These logos are not square and are not all the same shape: Team Liquid's is 294x294 and
      // Team Spirit's is 250x150. Forced into one square box with object-contain, the square one
      // filled it and the wide one letterboxed to 20x12 — so the same nominal size rendered Spirit
      // at 60% of Liquid's height and it read as a rendering fault. Constraining the height instead
      // makes every badge equally tall whatever its aspect, which is what Liquipedia does and what
      // makes a row of them look deliberate.
      //
      // Width is capped at three times the height so a freakishly wide logo cannot push a row out.
      style={{ height: size, width: 'auto', maxWidth: size * 3 }}
      className="shrink-0 rounded object-contain"
      onError={() => setBroken(true)}
    />
  )
}

/** Up to two letters, preferring word initials so "Team Falcons" reads TF rather than TE. */
function initials(name: string | null): string {
  if (!name) {
    return '?'
  }

  const words = name.split(/\s+/).filter(Boolean)
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  return name.slice(0, 2).toUpperCase()
}
